import dayjs from 'dayjs';
import type {
  FixedPriceAcceptOfferInDTO,
  LocationTimeslotQuoteDemandOutDTO,
  MakeOfferInDTO,
  MakeOfferLocationSlotInDTO,
  MakeOfferTransportLegInDTO,
  MyQuoteInfoOutDTO,
} from '@api/ringil-api.v2.tsx';
import type { Timeslot } from '@app/pages/carrier/demand-detail/CarrierQuote.tsx';
import type { CreateSpotState } from '@app/pages/carrier/demand-detail/DemandDetailComponent.tsx';
import type { DemandDetailView } from '@app/pages/carrier/demand-detail/types.ts';
import { buildNoteWithPhone, parsePhoneFromNote } from '@components/RingilComponents/InputPhoneNumber/InputPhoneNumber.tsx';
import {
  apiPragueFromDayjsLocal,
  apiPragueFromDayjsUtc,
  apiUtcFromDayjs,
  dayjsAtStartOfDayInTz,
  dayjsFromApiPrague,
  dayjsFromApiUtc,
  dayjsFromDayAndTimeStrings,
  formatTimeFromDayjsInTz,
  stringYMDFromDayjs,
} from '@utils/dateWithTz.tsx';

/**
 * RD-3062 — the form ↔ API mapping of the carrier's quote, moved out of `CarrierQuote` (form) and
 * `DemandDetailActions` (request bodies) unchanged so it can be unit-tested. The components keep the
 * hooks, API calls and UI; everything here is pure.
 */

export interface CarrierQuoteFormData {
  price: number;
  currency: string;
  unitPrice?: number;
  partialDeliveryUnits?: number;
  validTillLocalTime?: dayjs.Dayjs;
  validTillTimezone?: string;
  isConfirmed: boolean;
  note?: string;
  phonePrefix?: string;
  phoneNumber?: string;
  transportLegs?: MakeOfferTransportLegInDTO[];
  timeslots?: Timeslot[];
}

export interface CarrierQuoteInitialFormArgs {
  quote: DemandDetailView;
  /** `quote.carrierQuoteSettings.quoteValidTillReadOnly` */
  quoteValidTillReadOnly: boolean;
  /** `quote.carrierQuoteSettings.enforcePhoneNumber` */
  phoneEnabled: boolean;
  /** `offerType === OfferTypeEnum.SPOT` — the enum lives in a module that imports SVGs, so it stays in the component. */
  isSpotOffer: boolean;
  /** user default → quote user → quote → Europe/Prague */
  timezoneToUse: string;
}

/** The quote form as the carrier opens it: their previous quote if any, else the demand's defaults. */
export const carrierQuoteInitialForm = ({ quote, quoteValidTillReadOnly, phoneEnabled, isSpotOffer, timezoneToUse }: CarrierQuoteInitialFormArgs): CarrierQuoteFormData => ({
  price: quote.myQuoteInfo.myQuotePrice || 0,
  currency: quote.myQuoteInfo.myQuoteCurrency || quote.currency,
  unitPrice: quote.ladenFactor
    ? (quote.myQuoteInfo.myQuotePrice && (quote.myQuoteInfo.myQuoteManipulationUnits || quote.handlingUnitCount))
        ? quote.myQuoteInfo.myQuotePrice / (quote.myQuoteInfo.myQuoteManipulationUnits || quote.handlingUnitCount)
        : 0
    : undefined,
  partialDeliveryUnits: quote.myQuoteInfo.myQuoteManipulationUnits || quote.remainingManipulationUnits || quote.handlingUnitCount,
  validTillLocalTime: quoteValidTillReadOnly
    ? dayjs().add(quote.carrierQuoteSettings.carrierQuoteValidTillDefaultInHours ?? 24, 'hour')
    : (quote.myQuoteInfo.utcEnd ? dayjsFromApiUtc(quote.myQuoteInfo.utcEnd)
      : (quote.myQuoteInfo.myQuoteValidTill ? dayjsFromApiPrague(quote.myQuoteInfo.myQuoteValidTill) : dayjs().add(quote.carrierQuoteSettings.carrierQuoteValidTillDefaultInHours ?? 24, 'hour'))),
  validTillTimezone: timezoneToUse,
  isConfirmed: false,
  phonePrefix: (phoneEnabled && parsePhoneFromNote(quote.note)?.prefix) || '+420',
  phoneNumber: (phoneEnabled && parsePhoneFromNote(quote.note)?.number) || '',
  timeslots: isSpotOffer ? quote.timeslots
      .filter(timeslot => timeslot.timeslots?.length > 0)
      .map(timeslot => ({
    ...timeslot,
    timezoneId: timeslot.timeslots[0].timezoneId,
    useCustomerSlot: !timeslot.timeslots.some(timeslot => timeslot.isCarrier),
    customerTimeslots: timeslot.timeslots.filter(timeslot => !timeslot.isCarrier),
    carrierTimeslot: (() => {
      const carrier = timeslot.timeslots.find(ts => ts.isCarrier);
      const since = carrier?.utcStart ? dayjsFromApiUtc(carrier.utcStart) : dayjsFromApiPrague(carrier?.sinceInUTC);
      const till = carrier?.utcEnd ? dayjsFromApiUtc(carrier.utcEnd) : dayjsFromApiPrague(carrier?.tillInUTC);
      const timeFrom = since ? formatTimeFromDayjsInTz(since, carrier.timezoneId) : null;
      const timeTo = till ? formatTimeFromDayjsInTz(till, carrier.timezoneId) : null;
      const isWholeDay = since && till && since.hour() === 0 && since.minute() === 0 && till.hour() === 23 && till.minute() === 59;
      const dayString = stringYMDFromDayjs(dayjsAtStartOfDayInTz(since, timeslot.timeslots[0].timezoneId));
      return carrier
        ? {
          day: dayString,
          timeFrom: timeFrom,
          timeTo: timeTo,
          isWholeDay: isWholeDay,
        }
        : null;
    })(),
    }))
    : [],
  note: phoneEnabled ? (parsePhoneFromNote(quote.note)?.noteWithoutPhone ?? quote.note) : quote.note,
  transportLegs: quote.transportLegs,
});

/** What the quote form hands to `onAccept` — the carrier's offer, before it becomes a request body. */
export const buildCarrierQuoteAcceptData = (data: CarrierQuoteFormData, phoneEnabled: boolean, timezoneToUse: string): CreateSpotState => {
  const finalNote = phoneEnabled ? buildNoteWithPhone(data.phonePrefix, data.phoneNumber, data.note) : (data.note || undefined);
  return {
    price: data.price,
    currency: data.currency,
    selectedData: data.validTillLocalTime,
    utcEnd: data.validTillLocalTime,
    timezoneId: data.validTillTimezone || timezoneToUse,
    partialDeliveryUnits: data.partialDeliveryUnits ? data.partialDeliveryUnits : undefined,
    note: finalNote || undefined,
    transportLegs: data.transportLegs,
    timeslots: data.timeslots.map(timeslot => ({
      locationId: timeslot.locationId,
      useCustomerSlot: timeslot.useCustomerSlot,
      differentTimeslots: timeslot.useCustomerSlot
        ? []
        : timeslot.carrierTimeslot
          ? [{
            timeslotSince: timeslot.carrierTimeslot?.day && timeslot.carrierTimeslot?.timeFrom
              ? apiPragueFromDayjsLocal(dayjsFromDayAndTimeStrings(timeslot.carrierTimeslot.day, timeslot.carrierTimeslot.timeFrom, 'Europe/Prague'))
              : '',
            timeslotTill: timeslot.carrierTimeslot?.day && timeslot.carrierTimeslot?.timeTo
              ? apiPragueFromDayjsLocal(dayjsFromDayAndTimeStrings(timeslot.carrierTimeslot.day, timeslot.carrierTimeslot.timeTo, 'Europe/Prague'))
              : '',
            utcStart: timeslot.carrierTimeslot?.day && timeslot.carrierTimeslot?.timeFrom
              ? apiUtcFromDayjs(dayjsFromDayAndTimeStrings(timeslot.carrierTimeslot.day, timeslot.carrierTimeslot.timeFrom, timeslot.timezoneId))
              : '',
            utcEnd: timeslot.carrierTimeslot?.day && timeslot.carrierTimeslot?.timeTo
              ? apiUtcFromDayjs(dayjsFromDayAndTimeStrings(timeslot.carrierTimeslot.day, timeslot.carrierTimeslot.timeTo,timeslot.timezoneId))
              : '',
            timezoneId: timeslot.timezoneId,
          }]
          : [],
    })),
  };
};

/** Body of `caMakeQuote` for a spot demand. */
export const makeSpotOfferBody = (data: CreateSpotState): MakeOfferInDTO => ({
  price: data.price,
  currency: data.currency,
  validTill: apiPragueFromDayjsUtc(data.utcEnd, 'Europe/Prague'),
  utcEnd: apiUtcFromDayjs(data.utcEnd),
  timezoneId: data.timezoneId,
  units: data.partialDeliveryUnits,
  note: data.note,
  transportLegs: data.transportLegs,
  timeslots: data.timeslots,
});

/** Body of `caAcceptFixedPrice` — a fixed price only carries the note and the transport legs. */
export const acceptFixedPriceBody = (data: CreateSpotState): FixedPriceAcceptOfferInDTO => ({
  note: data.note,
  transportLegs: data.transportLegs,
});

/** Body of `caMakeAnotherNewQuote`: the carrier's previous quote, re-sent with its own timeslots. */
export const makeAnotherQuoteBody = (quote: MyQuoteInfoOutDTO, timeslots: LocationTimeslotQuoteDemandOutDTO[]): MakeOfferInDTO => ({
  price: quote.myQuotePrice,
  currency: quote.myQuoteCurrency,
  validTill: quote.myQuoteValidTill,
  units: quote.myQuoteManipulationUnits,
  timeslots: timeslots.map(locationQueueSlot => {
    return {
      locationId: locationQueueSlot.locationId,
      useCustomerSlot: locationQueueSlot.timeslots.findIndex(a => a.isCarrier) < 0,
      differentTimeslots: locationQueueSlot.timeslots
        .filter(a => a.isCarrier)
        .map(s => {
          return {
            timeslotSince: s.sinceInUTC,
            timeslotTill: s.tillInUTC,
            utcStart: s.utcStart,
            utcEnd: s.utcEnd,
            timezoneId: s.timezoneId,
          };
        }),
    } as MakeOfferLocationSlotInDTO;
  }),
  utcEnd: quote.utcEnd,
  timezoneId: quote.timezoneId,
});
